import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import Cropper, { type Area } from 'react-easy-crop'
import {
  cropImageToSquarePng,
  revokeObjectUrl,
  type CropArea,
} from '@/lib/profile-image'
import styles from './ProfilePhotoCropModal.module.css'

type ProfilePhotoCropModalProps = {
  fileName: string
  imageSrc: string | null
  isOpen: boolean
  onCancel: () => void
  onConfirm: (file: File) => void
}

/** Zoom minimo menor que 1 permite afastar em fotos grandes e ver mais contexto no recorte. */
const CROP_MIN_ZOOM = 0.18
/** Zoom maximo alto permite ampliar bem fotos pequenas ou detalhes. */
const CROP_MAX_ZOOM = 14
const CROP_ZOOM_SLIDER_STEP = 0.02

function normalizeCropArea(area: Area): CropArea {
  return {
    x: Math.round(area.x),
    y: Math.round(area.y),
    width: Math.round(area.width),
    height: Math.round(area.height),
  }
}

export function ProfilePhotoCropModal({
  fileName,
  imageSrc,
  isOpen,
  onCancel,
  onConfirm,
}: ProfilePhotoCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setError(null)
  }, [imageSrc, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isConfirming) {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isConfirming, isOpen, onCancel])

  useEffect(() => {
    if (!isOpen || !imageSrc || !croppedAreaPixels) return

    let cancelled = false
    let nextPreviewUrl: string | null = null

    const timer = window.setTimeout(() => {
      void cropImageToSquarePng(imageSrc, normalizeCropArea(croppedAreaPixels), {
        fileName,
        size: 320,
      })
        .then((file) => {
          nextPreviewUrl = URL.createObjectURL(file)
          if (cancelled) {
            revokeObjectUrl(nextPreviewUrl)
            return
          }

          setPreviewUrl((current) => {
            revokeObjectUrl(current)
            return nextPreviewUrl
          })
        })
        .catch(() => {
          if (!cancelled) {
            setPreviewUrl((current) => {
              revokeObjectUrl(current)
              return null
            })
          }
        })
    }, 120)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      if (nextPreviewUrl) {
        revokeObjectUrl(nextPreviewUrl)
      }
    }
  }, [croppedAreaPixels, fileName, imageSrc, isOpen])

  useEffect(() => {
    if (isOpen) return

    setPreviewUrl((current) => {
      revokeObjectUrl(current)
      return null
    })
  }, [isOpen])

  useEffect(() => {
    return () => {
      revokeObjectUrl(previewUrl)
    }
  }, [previewUrl])

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return

    setIsConfirming(true)
    setError(null)

    try {
      const file = await cropImageToSquarePng(
        imageSrc,
        normalizeCropArea(croppedAreaPixels),
        { fileName }
      )
      onConfirm(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nao foi possivel preparar a foto.')
    } finally {
      setIsConfirming(false)
    }
  }

  if (!isOpen || !imageSrc) return null

  return (
    <div className={styles.overlay} onClick={(event) => {
      if (event.target === event.currentTarget && !isConfirming) {
        onCancel()
      }
    }}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-photo-crop-title"
      >
        <div className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Foto da entidade</p>
            <h2 id="profile-photo-crop-title" className={styles.title}>
              Ajuste o recorte quadrado
            </h2>
            <p className={styles.subtitle}>
              A area enviada sera sempre quadrada. A previa ao lado mostra o encaixe final no avatar redondo.
            </p>
          </div>

          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            disabled={isConfirming}
            aria-label="Fechar editor de foto"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.cropPanel}>
            <div className={styles.cropShell}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                minZoom={CROP_MIN_ZOOM}
                maxZoom={CROP_MAX_ZOOM}
                zoomSpeed={1.35}
                showGrid
                cropShape="rect"
                objectFit="cover"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
              />
            </div>

            <label className={styles.zoomLabel} htmlFor="profile-photo-zoom">
              Zoom
            </label>
            <input
              id="profile-photo-zoom"
              type="range"
              min={CROP_MIN_ZOOM}
              max={CROP_MAX_ZOOM}
              step={CROP_ZOOM_SLIDER_STEP}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              className={styles.zoomInput}
            />
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.previewCard}>
              <span className={styles.previewLabel}>Previa principal</span>
              <div className={styles.previewCircle}>
                {previewUrl ? (
                  <img src={previewUrl} alt="Previa da foto da entidade" className={styles.previewImage} />
                ) : (
                  <div className={styles.previewFallback}>
                    <Icon icon="mdi:image-outline" />
                  </div>
                )}
              </div>

              <div className={styles.previewMiniRow}>
                <div className={styles.previewMini}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className={styles.previewImage} />
                  ) : (
                    <div className={styles.previewFallback}>
                      <Icon icon="mdi:image-outline" />
                    </div>
                  )}
                </div>
                <div className={styles.previewMeta}>
                  <strong>Avatar do sistema</strong>
                  <span>Mesma leitura dos moldes circulares do produto.</span>
                </div>
              </div>
            </div>

            <div className={styles.noteCard}>
              <span className={styles.noteTitle}>Regras</span>
              <p className={styles.noteText}>A foto final sera gerada em PNG quadrado para manter transparencias quando existirem.</p>
              <p className={styles.noteText}>Nada e enviado agora. O upload so acontece quando voce salvar as alteracoes do perfil.</p>
            </div>

            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}
          </aside>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onCancel}
            disabled={isConfirming}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void handleConfirm()}
            disabled={!croppedAreaPixels || isConfirming}
          >
            {isConfirming ? 'Preparando foto...' : 'Usar esta foto'}
          </button>
        </div>
      </div>
    </div>
  )
}
